import {createContext, useState, useEffect, useCallback} from 'react';
import {getSourceSite} from './url-helpers';

const WIDTH_BREAK = 1080;

interface ModalProps {
  canClose: boolean;
  [key: string]: unknown;
}

interface Modal {
  modalName: string | null;
  modalProps: ModalProps;
}

export interface GlobalUI {
  isMobile: boolean;
  modal: Modal;
  setModal: (modal: Modal) => void;
  openModal: (modalName: string, modalProps?: Partial<ModalProps>) => void;
  closeModal: () => void;
  email: string | null;
  setGlobalEmail: (email: string | null) => void;
  invitationId: string | null;
  setInvitationId: (invitationId: string | null) => void;
  body: unknown;
  setBody: (body: unknown) => void;
  redirectUrl: string | null;
  setRedirectUrl: (redirectUrl: string | null) => void;
  sourceSite: string | null;
  isCollaborator: boolean;
  setIsCollaborator: (isCollaborator: boolean) => void;
}

const DEFAULT_GLOBAL_UI = {
  isMobile: window.innerWidth <= WIDTH_BREAK,
  email: null as string | null,
  invitationId: null as string | null,
  body: null as unknown,
  redirectUrl: null as string | null,
  isCollaborator: false,
  sourceSite: null as string | null,
  modal: {
    modalName: null as string | null,
    modalProps: {
      canClose: true,
    },
  },
};

export const globalUIContext = createContext<GlobalUI>({} as GlobalUI);

export const useGlobalUI = (): GlobalUI => {
  const [isMobile, setIsMobile] = useState<boolean>(DEFAULT_GLOBAL_UI.isMobile);

  const [email, setEmail] = useState<string | null>(DEFAULT_GLOBAL_UI.email);
  const [invitationId, setInvitationId] = useState<string | null>(
    DEFAULT_GLOBAL_UI.invitationId
  );
  const [body, setBody] = useState<unknown>(DEFAULT_GLOBAL_UI.body);
  const [modal, setModal] = useState<Modal>(DEFAULT_GLOBAL_UI.modal);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(
    DEFAULT_GLOBAL_UI.redirectUrl
  );
  const [sourceSite, setSourceSite] = useState<string | null>(
    DEFAULT_GLOBAL_UI.sourceSite
  );
  const [isCollaborator, setIsCollaborator] = useState<boolean>(
    DEFAULT_GLOBAL_UI.isCollaborator
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= WIDTH_BREAK);
    }
    window.addEventListener('resize', handleResize);
    setSourceSite(getSourceSite());

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const openModal = useCallback(
    (modalName: string, modalProps: Partial<ModalProps> = {}) =>
      setModal({
        modalName,
        modalProps: {
          ...DEFAULT_GLOBAL_UI.modal.modalProps,
          ...modalProps,
        },
      }),
    []
  );

  const closeModal = useCallback(() => setModal(DEFAULT_GLOBAL_UI.modal), []);

  return {
    isMobile,
    modal,
    setModal,
    openModal,
    closeModal,
    email,
    setGlobalEmail: setEmail,
    invitationId,
    setInvitationId,
    body,
    setBody,
    redirectUrl,
    setRedirectUrl,
    sourceSite,
    isCollaborator,
    setIsCollaborator,
  };
};
